<template>
<v-form v-model="valid" ref="form">
	<v-card>
        <v-card-text class="pt-0">
              <v-card-title class="px-0">
					<span>
						{{$t('AccountCreateEdit.account-info')}}
					</span>
					<v-spacer></v-spacer>
					<bee-btn
					    context="account"
					    :action-name="actions.EDIT"
					    :content="$t('AccountCreateEdit.save')"
					    icon="fa-user-check"
					    color="primary"
					    @click="save()"
					    :loading="loading"
					    :disabled="!valid || file_is_locked"
					    outer-class="mx-3"
					></bee-btn>
					<bee-btn
					    context="account"
					    :action-name="actions.GET"
					    :content="$t('AccountCreateEdit.cancel')"
					    color="deletingColor"
					    :loading="loading"
					    @click="cancel()"
					></bee-btn>
				</v-card-title>
                <v-layout align-start align-content-center justify-space-between row wrap>
					<v-col cols="12" lg="4" md="4" sm="6" xs="12" class="py-1">
						<bee-text-field
							:label="$t('AccountCreateEdit.account-name')"
							:rules="[rules.required]"
							v-model="account.name"
							:action-name="actions.CREATE" context="account"
						></bee-text-field>
					</v-col>
					<v-col cols="12" lg="4" md="4" sm="6" xs="12" class="py-1">
						<bee-account-autocomplete
							context="account" :action-name="actions.CREATE"
							:label="$t('AccountCreateEdit.main-account')"
							color="primary"
							v-model="account.parent_account_id"
							:items="configs.accounts.filter(c=>c.type == 1)"
							@change="change()"
							:clearable="true"
							:with-balance="true"
						></bee-account-autocomplete>
					</v-col>
					<v-col cols="12" lg="4" md="4" sm="6" xs="12" class="py-1">
						<bee-accounting-no-field 
							ref="accountNum"
							:prefix="prefix"
							:rules="[rules.required]"
							v-model="account.inner_accounting_no"
						></bee-accounting-no-field>
					</v-col>
					<v-col cols="12" lg="4" md="4" sm="6" xs="12" class="py-1">
						<bee-autocomplete
							context="account" :action-name="actions.CREATE"
							color="primary"
							v-model="account.type"
							@change="onTypeChanged()"
							:label="$t('AccountCreateEdit.account-type.title')"
							:items="configs.account_types.filter(c=>c.value).map(c=>({...c,
								name:
								c.value == 1 ? $t('AccountCreateEdit.account-type.main') :
								c.value == 2 ? $t('AccountCreateEdit.account-type.sub') :
								c.name })) || []"
							item-text="name"
							:rules="[rules.required]"
							item-value="value"
						></bee-autocomplete>
					</v-col>
					<v-col cols="12" lg="4" md="4" sm="6" xs="12" class="py-1">
						<bee-autocomplete
							context="account" :action-name="actions.CREATE"
							:label="$t('AccountCreateEdit.final-accounts.title')"
							v-model="account.final_type"
							:items="configs.final_account_types.filter(c=>c.value).map(c=>({...c,
								name:
								c.value == 1 ? $t('AccountCreateEdit.final-accounts.operations') :
								c.value == 2 ? $t('AccountCreateEdit.final-accounts.trade') :
								c.value == 3 ? $t('AccountCreateEdit.final-accounts.gains-and-losses') :
								c.value == 4 ? $t('AccountCreateEdit.final-accounts.budget') :
								c.name })) || []"
							itemText="name"
							item-value="value"
						></bee-autocomplete>
					</v-col>
					<v-col cols="12" lg="4" md="4" sm="6" xs="12" class="py-1">
						<bee-multi-select
							context="account" :action-name="actions.CREATE"
							:label="$t('AccountCreateEdit.custom-statement')"
							v-model="account.custom_statement_ids"
							:items="custom_statements"
							item-text="name"
							item-value="id"
							:disabled="account.type == 1"
						></bee-multi-select>
					</v-col>
					<v-col cols="12" lg="4" md="4" sm="6" xs="12" class="py-1">
						<bee-money-field
						    context="account"
						    :action-name="actions.CREATE"
						    :label="$t('AccountCreateEdit.max-debit-amount')"
						    v-model="account.max_debit_amount"
						    :rules="[rules.money]"
						></bee-money-field>
					</v-col>
					<v-col cols="12" lg="4" md="4" sm="6" xs="12" class="py-1">
						<bee-money-field
						    context="account"
						    :action-name="actions.CREATE"
							:label="$t('AccountCreateEdit.max-credit-amount')"
						    v-model="account.max_credit_amount"
						    :rules="[rules.money]"
						></bee-money-field>
					</v-col>
					<v-col cols="12" lg="4" md="4" sm="12" xs="12" class="d-flex justify-start py-1">
						<bee-checkbox
							class="mx-3 mt-1"
							:label="$t('AccountCreateEdit.debt-is-forbidden')"
							v-model="account.is_debt_forbidden"
						></bee-checkbox>
						<div v-if="usePinnedAccounts" class="d-flex align-center">
							<v-checkbox
								v-model="account.is_pinned"
								dense
								:label="$t('AccountCreateEdit.pinned')"
							>
							</v-checkbox>
							<v-menu offset-y max-width="250" open-on-hover>
								<template v-slot:activator="{ on, attrs }">
									<i
										v-bind="attrs"
										v-on="on"
										class="fas fa-info-circle mx-2 primary--text"
									/>
								</template>
								<div class="pa-2 text-body-2" :style="'background: rgba(255,255,255,0.7)'">
									<span>{{$t('AccountCreateEdit.pinned-description')}}</span>
								</div>
							</v-menu>
						</div>

					</v-col>
					<v-col cols="12" lg="12" md="12" sm="12" xs="12" class="py-1">
						<bee-text-field
							:label="$t('AccountCreateEdit.notes')"
							v-model="account.notes"
							:action-name="actions.CREATE" 
							context="account"
						></bee-text-field>
					</v-col>
                </v-layout>
				<v-card-title class="pt-0 px-0">
					{{$t('AccountCreateEdit.contect-info.title')}}
				</v-card-title>
                <v-layout align-start align-content-center justify-space-between row wrap>
					<v-col cols="12" lg="4" md="4" sm="6" xs="12" class="py-1">
						<bee-text-field
							:label="$t('AccountCreateEdit.contect-info.mobile-number')"
							v-model="contact_info.mobile_number"
							:rules="[rules.phone]"
							:action-name="actions.CREATE" context="account"
						></bee-text-field>
					</v-col>
					<v-col cols="12" lg="8" md="8" sm="6" xs="12" class="py-1">
						<bee-text-field
							:label="$t('AccountCreateEdit.contect-info.phone-number')"
							:rules="[rules.phone]"
							v-model="contact_info.phone_number "
							:action-name="actions.CREATE" context="account"
						></bee-text-field>
					</v-col>
					<v-col cols="12" lg="4" md="4" sm="6" xs="12" class="py-1">
						<bee-text-field
							:label="$t('AccountCreateEdit.contect-info.email')"
							:rules="[rules.email]"
							v-model="contact_info.email"
							:action-name="actions.CREATE" context="account"
						></bee-text-field>
					</v-col>
					<v-col cols="12" lg="8" md="8" sm="6" xs="12" class="py-1">
							<bee-text-field
								:label="$t('AccountCreateEdit.contect-info.address')"
								v-model="contact_info.address"
								:action-name="actions.CREATE" context="account"
							></bee-text-field>
					</v-col>
                </v-layout>
			</v-card-text>
    </v-card>
</v-form>
</template>

<script>
import rules from "../../../../validation";
import { mapGetters, mapState } from "vuex";
import  store  from "../../../../store/index.js";
// import EventBus from '../../../../event-bus'
import actions from "../../../../permissions/actions-names.js"
import { usePinnedAccounts } from '@/configs/global.js'
export default {
   data: () => ({
	actions,
	usePinnedAccounts,
    valid:false,
    account: {
		accounting_no: null,
		custom_statement_ids: null,
		name: null,
		inner_accounting_no: null,
		is_box: null,
		type: null,
		final_type: null,
		max_credit_amount: null,
		max_debit_amount: null,
		is_debt_forbidden: false,
		notes: null,
		parent_account_id: null,
		id: null,
		contact_info: null,
		is_pinned: false
    },
	...rules,
	loading : false,
	selectedId : null,
	contact_info: {
		id: null,
		mobile_number: null,
		phone_number: null,
		address: null,
		email: null,
	},
	default_account:{
		accounting_no: null,
		name: null,
		inner_accounting_no: null,
		is_box: null,
		type: null,
		final_type: null,
		max_credit_amount: {
			amount: null,
			currency_id: null
		},
		max_debit_amount: {
			amount: null,
			currency_id: null
		},
		is_debt_forbidden: null,
		notes: null,
		parent_account_id: null,
		id: null,
		contact_info: null
	},
	default_contact_info: {
		id: null,
		mobile_number: null,
		phone_number: null,
		address: null,
		email: null,
		account_id: null
	},
  }),  
  watch: {
	'$route.params.id'(val) {
		this.$store.dispatch('accounts/fetch', { id: val })
			.then(data => {
				this.account = data;
				this.contact_info = data.contact_info;
			})
	},
	'$route.params.parentId'(val) {
		this.account.parent_account_id = Number(val);
		this.change()
	}
  },
  mounted(){
	this.loading = false
	this.selectedId = Number(this.$route.params.id)
    if (!this.selectedId) // create account
		if(Number(this.$route.params.parentId))
		{
			this.account.parent_account_id = Number(this.$route.params.parentId)
			this.change()
		}
  },
  computed: {
	...mapGetters("configs", ["getAccountById"]),
	...mapState("configs", ["configs"]),
	...mapState("customStatements" , ["custom_statements"]),
	formTitle(){
		return this.selectedId ? this.$t('AccountCreateEdit.edit-account') : this.$t('AccountCreateEdit.create-account');
	},
	prefix(){
		if(this.account.parent_account_id)
			return this.configs.accounts.find(c=>c.id == this.account.parent_account_id).accounting_no.toString()
		return null },
	file_is_locked(){
        return this.configs?.financial_file?.is_locked }
	},
methods:{
	save(){
	this.loading = true;
	this.account.contact_info = {...this.contact_info}
	if(this.account.max_credit_amount?.amount)
		this.account.max_credit_amount.currency_id = this.configs.financial_file.default_currency_id 
	else
		this.account.max_credit_amount = null
	if(this.account.max_debit_amount?.amount)
		this.account.max_debit_amount.currency_id = this.configs.financial_file.default_currency_id 
	else
		this.account.max_debit_amount = null
	if (this.selectedId)
	{	
		store.dispatch("accounts/update", this.account)
		.then((data) => 
			{ 
				Promise.all([
					store.dispatch("configs/fetchAll"),
					store.dispatch("accounts/fetchTree"),
					store.dispatch("customStatements/fetchAll")
				]).then(() => {
					this.$emit('reload-accounts-tree')
					this.$router.replace({ name: 'account', params: { id: data.id } })
					this.reSet()
				});
			})
		.finally(() => { this.loading = false});
	}
    else {
        store.dispatch("accounts/create", this.account)
			.then((id) => {
				Promise.all([
					store.dispatch("configs/fetchAll"),
					store.dispatch("accounts/fetchTree"),
					store.dispatch("customStatements/fetchAll")
				]).then(() => {
					this.$emit('reload-accounts-tree')
					this.$router.replace({ name: 'account', params: { id: id } })
					this.reSet()
				});
			})
			.finally(() => {this.loading = false });
        }
      
    },
    cancel(){
		this.$router.push({ name: "accounts-tree"}).catch(()=>{})
	},
	change(){
		this.account.final_type = this.getAccountById(Number(this.account.parent_account_id)).final_type;
		this.$nextTick(()=> {
			this.$refs.accountNum.setFocus();
			if(this.account.parent_account_id)
				store.dispatch("accounts/GenerateNewAccountNo", this.account.parent_account_id).then(response=>{
					this.account.inner_accounting_no = response
				})
		});
	},
	assignMoneyAmount(amount, currency_id){
      if(amount)
        return {amount, currency_id}
      return null
	},
	onTypeChanged(){
		if(this.account.type == 1)
			this.account.custom_statement_ids = null
	},
	reSet(){
		this.account = {...this.default_account}
		this.contact_info = {...this.default_contact_info}
		this.$refs.form.resetValidation()
	}
},
  async beforeRouteEnter(to, from, next) {
    
    next(vm => {
		if(to.params.id) 
		{	
			store.dispatch("accounts/fetch", { id: to.params.id }).then(response => { 
				vm.account = {...response}; 
				// console.log(vm.account);
				vm.contact_info = {...response.contact_info} 
		})
		}
	})

  }
};
</script>